/* Magnetik cart drawer.
 *
 * Opens on add-to-cart and on the header cart icon, instead of sending the
 * customer to a full cart page.
 *
 * After any mutation the drawer's HTML is re-fetched through Shopify's Section
 * Rendering API, so money, discount allocations and totals are always rendered
 * by Liquid rather than recomputed here.
 */
window.MagnetikDrawer = (function () {
  'use strict';

  var SEL = '#cart-drawer';
  var sectionId = null;      // resolved from the DOM, not hardcoded
  var lastFocus = null;
  var busy = false;

  function root() { return document.querySelector(SEL); }

  /* The drawer is registered inside the header section group, so its real
     section id looks like "sections--123__magnetik_cart_drawer". Read it off
     the wrapper rather than guessing. */
  function resolveSectionId() {
    if (sectionId) return sectionId;
    var el = root();
    while (el && el !== document.body) {
      if (el.id && el.id.indexOf('shopify-section-') === 0) {
        sectionId = el.id.replace('shopify-section-', '');
        return sectionId;
      }
      el = el.parentElement;
    }
    return null;
  }

  function isOpen() {
    var r = root();
    return !!r && r.classList.contains('open');
  }

  function open() {
    var r = root();
    if (!r) return;
    lastFocus = document.activeElement;
    r.classList.add('open');
    r.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-locked');
    var focusable = r.querySelector('.drawer-x');
    if (focusable) focusable.focus();
  }

  function close() {
    var r = root();
    if (!r) return;
    r.classList.remove('open');
    r.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-locked');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* Pull fresh drawer HTML from Shopify and swap the inner markup in.
     Gifts and the bundle code go on first, so the HTML that comes back
     already has Liquid's real totals in it. */
  function refresh() {
    var id = resolveSectionId();
    if (!id) return Promise.resolve();

    return getCart()
      .then(function (cart) {
        return syncGifts(cart).then(function (changed) { return changed ? getCart() : cart; });
      })
      .then(syncBundle)
      .catch(function () {})
      .then(function () { return render(id); });
  }

  function render(id) {

    /* Carry preview params through. Without preview_theme_id the section is
       rendered from the PUBLISHED theme, which has no drawer, so the fetch
       would come back empty in any preview session. */
    var q = new URLSearchParams(window.location.search);
    var out = new URLSearchParams();
    ['preview_theme_id', 'view', '_ab', '_fd', '_sc'].forEach(function (k) {
      if (q.has(k)) out.set(k, q.get(k));
    });
    out.set('section_id', id);
    var url = window.location.pathname + '?' + out.toString();

    return fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector(SEL);
        var cur = root();
        if (!fresh || !cur) return;
        var wasOpen = cur.classList.contains('open');
        cur.innerHTML = fresh.innerHTML;
        if (wasOpen) { cur.classList.add('open'); cur.setAttribute('aria-hidden', 'false'); }
        return syncCount();
      })
      .catch(function () {});
  }

  /* ---------------- free gifts ----------------
     Gifts are real products added to the cart as their own lines, tagged with
     a hidden _gift property. They ring up at $0 because of Buy X Get Y
     automatic discounts in Shopify admin -- Shopify, not this file, decides
     the price, so a gift can never be free without qualifying.

     This file only keeps the gift lines in step with the cart:
       - enough bundle units and the deal is live -> exactly one of each gift
       - not enough units, or the deal is over    -> gift line removed
     and one safety net: if a gift line is NOT at $0 (discount missing, ended,
     or edited in admin), it is taken back out, so nobody is ever charged for
     a "free" gift. That block lasts for this page load, so it cannot loop. */
  var giftCfg = (function () {
    var el = document.getElementById('mgk-gift-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  })();
  var giftBlocked = {};

  function dealLive() {
    if (!giftCfg || !giftCfg.on || !giftCfg.bundle) return false;
    var end = Date.parse(giftCfg.end || '');
    return isNaN(end) ? true : Date.now() < end;
  }

  function giftKey(item) {
    return (item && item.properties && item.properties._gift) || '';
  }

  function postJSON(path, body) {
    var base = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
    return fetch(base + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(path + ' ' + r.status);
      return r.json();
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* Remove any gift line that is not actually free. Resolves true if it did. */
  function guardGifts(cart) {
    var bad = {}, any = false;
    ((cart && cart.items) || []).forEach(function (it) {
      var k = giftKey(it);
      if (k && it.final_line_price > 0) { bad[it.key] = 0; giftBlocked[k] = true; any = true; }
    });
    if (!any) return Promise.resolve(false);
    return postJSON('/cart/update.js', { updates: bad }).then(function () { return true; });
  }

  /* Resolves true when the cart changed. Never rejects. */
  function syncGifts(cart) {
    if (!giftCfg || !cart || !cart.items) return Promise.resolve(false);

    var live = dealLive();
    var units = 0;
    cart.items.forEach(function (it) {
      if (it.product_id === giftCfg.bundle && !giftKey(it)) units += it.quantity;
    });

    var updates = {}, adds = [], touched = false;
    (giftCfg.gifts || []).forEach(function (g) {
      var want = (live && !giftBlocked[g.key] && units >= g.min) ? 1 : 0;
      var have = 0;
      cart.items.forEach(function (it) {
        if (giftKey(it) !== g.key) return;
        var keep = Math.max(0, Math.min(it.quantity, want - have));
        if (keep !== it.quantity) { updates[it.key] = keep; touched = true; }
        have += keep;
      });
      if (want > have && g.variant) {
        adds.push({ id: g.variant, quantity: want - have, properties: { _gift: g.key } });
      }
    });

    var changed = touched || adds.length > 0;
    var chain = Promise.resolve();
    if (touched) chain = chain.then(function () { return postJSON('/cart/update.js', { updates: updates }); });
    if (adds.length) chain = chain.then(function () { return postJSON('/cart/add.js', { items: adds }); });

    return chain
      .then(function () { return changed ? getCart() : cart; })
      .then(guardGifts)
      .then(function (guarded) { return changed || guarded; })
      .catch(function () { return changed; });
  }

  /* ---------------- bundle pricing ----------------
     The advertised 2-pack / 3-pack prices are produced by a Shopify discount,
     not by any maths in this file. Rather than only attaching the code on the
     way to checkout, put it on the CART, so the drawer, the cart page and
     checkout all show the one number Shopify actually charges.

     Shopify then owns the rule in both directions: it enforces the code's own
     minimum-quantity requirement (a 3-pack code cannot stick to a one-unit
     cart) and it re-validates on every quantity change, so a customer cannot
     hold a bundle price they no longer qualify for.

     Blank code settings make every function here a no-op, which is exactly
     what happens once automatic discounts exist in Admin. */

  function codeFor(count) {
    var r = root();
    if (!r) return '';
    if (count >= 3) return r.getAttribute('data-three-code') || '';
    if (count >= 2) return r.getAttribute('data-two-code') || '';
    return '';
  }

  /* Which code is on the cart right now.

     cart.discount_codes is the only reliable source: the bundle discount is an
     "amount off products" discount, so Shopify allocates it per LINE and
     cart_level_discount_applications stays empty. Reading only that array made
     this function always return '', so syncBundle re-applied the code on every
     single load -- which is what put the cart page in a reload loop. */
  function appliedCode(cart) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && codes[i].code && codes[i].applicable !== false) {
        return String(codes[i].code).toUpperCase();
      }
    }
    var apps = (cart && cart.cart_level_discount_applications) || [];
    for (var j = 0; j < apps.length; j++) {
      if (apps[j].title) return String(apps[j].title).toUpperCase();
    }
    return '';
  }

  /* Resolves true when the cart's code actually changed, so a caller that
     renders server-side (the cart page) knows it needs to reload. Never
     rejects: a failed discount fetch must not block the cart from rendering. */
  function syncBundle(cart) {
    var want = codeFor(cart ? cart.item_count : 0);
    if (!want || appliedCode(cart) === want.toUpperCase()) return Promise.resolve(false);

    return fetch('/discount/' + encodeURIComponent(want) + '?redirect=/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function () { return true; })
      .catch(function () { return false; });
  }

  /* Read the cart, put the right code on it, report whether anything moved. */
  function syncBundleNow() {
    var giftsChanged = false;
    return getCart()
      .then(function (cart) {
        return syncGifts(cart).then(function (changed) {
          giftsChanged = changed;
          return changed ? getCart() : cart;
        });
      })
      .then(syncBundle)
      .then(function (codeChanged) { return giftsChanged || codeChanged; })
      .catch(function () { return giftsChanged; });
  }

  function syncCount() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var els = document.querySelectorAll('[data-cart-count]');
        for (var i = 0; i < els.length; i++) {
          els[i].textContent = c.item_count;
          els[i].setAttribute('data-empty', c.item_count === 0 ? 'true' : 'false');
        }
        return c;
      })
      .catch(function () {});
  }

  function change(key, quantity) {
    if (busy) return Promise.resolve();
    busy = true;
    var r = root();
    if (r) r.classList.add('drawer-busy');

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(base.replace(/\/$/, '') + '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) { if (!res.ok) throw new Error('change failed'); return res.json(); })
      .then(function () { return refresh(); })
      .catch(function () {})
      .then(function () {
        busy = false;
        var rr = root();
        if (rr) rr.classList.remove('drawer-busy');
      });
  }

  /* Delegated so it survives the innerHTML swap on every refresh. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest('[data-drawer-close]')) {
      var link = t.closest('a[href]');
      if (!link) { e.preventDefault(); }
      close();
      return;
    }

    var q = t.closest('.qty-up, .qty-down, .cart-remove');
    if (q && q.closest(SEL)) {
      e.preventDefault();
      change(q.getAttribute('data-key'), parseInt(q.getAttribute('data-q'), 10));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) close();
  });

  /* Checkout: keep the bundle-code stopgap until automatic discounts exist. */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.id !== 'drawer-form') return;
    if (f.getAttribute('data-discounted') === 'true') return;

    var count = parseInt(f.getAttribute('data-count'), 10) || 0;
    var code = count >= 3 ? f.getAttribute('data-three-code')
             : count >= 2 ? f.getAttribute('data-two-code')
             : '';
    if (!code) return;

    e.preventDefault();
    window.location.href = '/discount/' + encodeURIComponent(code) +
                           '?redirect=' + encodeURIComponent('/checkout');
  });

  /* Header cart icon opens the drawer rather than navigating. */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('.nav-cart');
    if (!a) return;
    if (!root()) return;   // no drawer on this page, let the link navigate
    e.preventDefault();
    refresh().then(open);
  });

  syncCount();

  return {
    open: open,
    close: close,
    refresh: refresh,
    syncCount: syncCount,
    syncBundleNow: syncBundleNow,
    dealLive: dealLive
  };
})();
